import type { InputStatus } from 'antd/lib/_util/statusUtils';
import type { BaseType } from 'antd/lib/typography/Base';
import { type JSX, useCallback, useEffect, useMemo } from 'react';

import { ValidationError } from 'src/components/base/fields/common/ValidationError/ValidationError';
import type {
  IntervalFieldValue,
  IntervalValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField.types';
import { SpecificationIntervalMultiselect } from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalMultiselect';
import { withField } from 'src/helpers';
import { type IWithFieldErrorProps, withFieldError } from 'src/helpers/withFieldError';
import type { IFieldProps } from 'src/types';
import type { IProductSpecificationFieldReference } from 'src/types/reference/IProductSpecificationReference';

type SpecificationIntervalFieldProps = IFieldProps &
  Omit<IWithFieldErrorProps, 'isShowErrorMessage'> & {
    specificationField: IProductSpecificationFieldReference;

    containerClassName?: string;
    className?: string;
    errorType: BaseType;
    status?: InputStatus;
    disabled?: boolean;
  };

export const SpecificationIntervalMultiselectField = withField(
  withFieldError(
    ({
      field: formikField,
      form: { setFieldValue, setFieldTouched },
      touchOnMount,

      specificationField,
      containerClassName,
      className,
      errorType,
      status,
      disabled,
    }: Readonly<SpecificationIntervalFieldProps>): JSX.Element => {
      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      const defaultValue: IntervalValue = useMemo(() => {
        const fieldValue = formikField.value as unknown as IntervalFieldValue;

        if (fieldValue === undefined) {
          return specificationField.multiselect ? [] : null;
        }

        if (fieldValue === null && specificationField.multiselect) {
          return [];
        }

        return Array.isArray(fieldValue) ? fieldValue.filter(Boolean) : fieldValue;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const handleChange = useCallback(
        async (selectedValue: IntervalValue) => {
          await setFieldValue(formikField.name, selectedValue);
          await setFieldTouched(formikField.name, true);
        },
        [setFieldValue, formikField.name, setFieldTouched]
      );

      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      useEffect(() => {
        if (touchOnMount) {
          void setFieldTouched(formikField.name, true);
        }
      }, []);

      return (
        <div className="flex column nowrap align-items-stretch gap-tiny">
          <SpecificationIntervalMultiselect
            defaultValue={defaultValue}
            onChange={handleChange}
            field={specificationField}
            containerClassName={containerClassName}
            className={className}
            status={status}
            disabled={disabled}
          />
          <div className="relative">
            <ValidationError name={formikField.name} errorType={errorType} />
          </div>
        </div>
      );
    }
  )
);
